import { CRMData } from '../types';

export const mockData: CRMData = {
  "metadata": {
    "description": "Complete Mock CRM Data for Doctor 360° Mini-Dashboard",
    "lastUpdated": "2024-12-31",
    "purpose": "Doctor profile, visit history, engagement analytics, and AI persona generation",
    "dataModels": ["doctors", "visits", "products", "specialties"]
  },
  "specialties": [
    {
      "id": "cardiology",
      "name": "Cardiology",
      "subSpecialties": ["Interventional Cardiology", "Heart Failure & Transplant", "Electrophysiology", "Preventive Cardiology"],
      "relevantProducts": ["CardioMax", "DiabeCare"],
      "avgConsultTime": 15,
      "keyTopics": ["Hypertension management", "Heart failure protocols", "Cardiovascular risk factors", "Statin therapy"]
    }
  ],
  "products": [
    {
      "id": "cardiomax",
      "name": "CardioMax",
      "category": "Cardiovascular",
      "genericName": "Amlodipine + Atorvastatin",
      "indication": "Hypertension with dyslipidemia",
      "dosage": "5/10mg, 5/20mg, 10/10mg, 10/20mg",
      "keyBenefits": ["Dual action in single pill", "24-hour BP control", "LDL reduction up to 45%"],
      "clinicalTrials": ["ASCOT-LLA", "CAMELOT"],
      "competitorProducts": ["Caduet", "Amlodac-AT"],
      "sampleSKU": "CMax-Sample-10",
      "color": "#ef4444"
    },
    {
      "id": "diabecare",
      "name": "DiabeCare",
      "category": "Diabetes",
      "genericName": "Metformin + Sitagliptin",
      "indication": "Type 2 Diabetes Mellitus",
      "dosage": "500/50mg, 1000/50mg",
      "keyBenefits": ["Complementary mechanisms", "Weight neutral", "Low hypoglycemia risk"],
      "clinicalTrials": ["TECOS", "HARMONY"],
      "competitorProducts": ["Janumet", "Istamet"],
      "sampleSKU": "DCare-Sample-10",
      "color": "#f59e0b"
    },
    {
      "id": "neuroplex",
      "name": "NeuroPlex",
      "category": "Neurology",
      "genericName": "Levetiracetam Extended Release",
      "indication": "Epilepsy, neuropathic conditions",
      "dosage": "500mg, 750mg, 1000mg XR",
      "keyBenefits": ["Once daily dosing", "Minimal drug interactions", "Favorable cognitive profile"],
      "clinicalTrials": ["N01252", "KEEPER"],
      "competitorProducts": ["Keppra", "Levipil"],
      "sampleSKU": "NPlex-Sample-10",
      "color": "#8b5cf6"
    },
    {
      "id": "immunoshield",
      "name": "ImmunoShield",
      "category": "Immunology",
      "genericName": "Zinc + Vitamin C + Vitamin D3",
      "indication": "Immune support, prophylaxis",
      "dosage": "Once daily effervescent",
      "keyBenefits": ["Triple immune support", "Pleasant taste", "High bioavailability"],
      "clinicalTrials": ["ZIVC-2021"],
      "competitorProducts": ["Zincovit", "Supradyn"],
      "sampleSKU": "IShield-Sample-15",
      "color": "#10b981"
    },
    {
      "id": "respiclear",
      "name": "RespiClear",
      "category": "Respiratory",
      "genericName": "Montelukast + Levocetirizine",
      "indication": "Allergic rhinitis, asthma",
      "dosage": "10/5mg tablet",
      "keyBenefits": ["Day and night symptom control", "Non-drowsy formula", "Once daily convenience"],
      "clinicalTrials": ["COMPACT", "MONICA"],
      "competitorProducts": ["Montair-LC", "Montek-LC"],
      "sampleSKU": "RClear-Sample-10",
      "color": "#3b82f6"
    },
    {
      "id": "gastroease",
      "name": "GastroEase",
      "category": "Gastroenterology",
      "genericName": "Pantoprazole + Domperidone SR",
      "indication": "GERD, Dyspepsia",
      "dosage": "40/30mg capsule",
      "keyBenefits": ["Dual action relief", "Sustained release prokinetic", "Rapid symptom control"],
      "clinicalTrials": ["GASTRO-RELIEF"],
      "competitorProducts": ["Pan-D", "Pantocid-DSR"],
      "sampleSKU": "GEase-Sample-10",
      "color": "#ec4899"
    },
    {
      "id": "sleepwell",
      "name": "SleepWell",
      "category": "CNS",
      "genericName": "Melatonin + L-Theanine",
      "indication": "Sleep disorders, jet lag",
      "dosage": "3mg/200mg tablet",
      "keyBenefits": ["Non-habit forming", "Natural sleep architecture", "No morning grogginess"],
      "clinicalTrials": ["SLEEP-NAT"],
      "competitorProducts": ["Meloset", "Circadin"],
      "sampleSKU": "SWell-Sample-15",
      "color": "#6366f1"
    }
  ],
  "doctors": [
    {
      "id": "DOC-001",
      "personalInfo": {
        "name": "Dr. Anil Mehta",
        "age": 52,
        "gender": "Male",
        "photo": "https://api.dicebear.com/7.x/personas/svg?seed=AnilMehta&backgroundColor=c0aede"
      },
      "professionalInfo": {
        "specialty": "Cardiology",
        "subSpecialty": "Interventional Cardiology",
        "tier": "A",
        "experience": 25,
        "medicalSchool": "Grant Medical College, Mumbai",
        "fellowships": ["American College of Cardiology", "Cardiological Society of India"],
        "registrationNumber": "MH-12345"
      },
      "practiceInfo": {
        "clinic": "Heart Care Clinic",
        "hospital": "Lilavati Hospital",
        "address": "Turner Road, Bandra West, Mumbai 400050",
        "territory": "Mumbai West",
        "coordinates": { "lat": 19.0596, "lng": 72.8295 }
      },
      "contactInfo": {
        "phone": "+91-22-2640-1234",
        "email": "dr.mehta@heartcare.in",
        "assistant": "Ms. Priya",
        "assistantPhone": "+91-22-2640-1235"
      },
      "preferences": {
        "preferredTime": "10:00 AM - 12:00 PM",
        "preferredDays": ["Tuesday", "Thursday"],
        "preferredContactMethod": "In-person",
        "languages": ["English", "Hindi", "Marathi"],
        "communicationNotes": "Prefers detailed clinical data. Appreciates research papers."
      },
      "practiceMetrics": {
        "patientVolume": 120,
        "avgConsultTime": 15,
        "prescriptionPotential": "Very High",
        "influenceLevel": "Key Opinion Leader"
      },
      "productAffinities": {
        "currentlyPrescribing": ["CardioMax", "DiabeCare"],
        "interested": ["NeuroPlex"],
        "competitor": ["Caduet"]
      }
    },
    {
      "id": "DOC-002",
      "personalInfo": {
        "name": "Dr. Sunita Patel",
        "age": 45,
        "gender": "Female",
        "photo": "https://api.dicebear.com/7.x/personas/svg?seed=SunitaPatel&backgroundColor=ffd5dc"
      },
      "professionalInfo": {
        "specialty": "General Medicine",
        "subSpecialty": "Internal Medicine",
        "tier": "A",
        "experience": 18,
        "medicalSchool": "KEM Hospital, Mumbai",
        "fellowships": ["Association of Physicians of India"],
        "registrationNumber": "MH-23456"
      },
      "practiceInfo": {
        "clinic": "Patel Medical Center",
        "hospital": "Kokilaben Hospital",
        "address": "Lokhandwala Complex, Andheri West, Mumbai 400053",
        "territory": "Mumbai West",
        "coordinates": { "lat": 19.1136, "lng": 72.8697 }
      },
      "contactInfo": {
        "phone": "+91-22-2632-5678",
        "email": "dr.sunita@patelmedical.in",
        "assistant": "Mr. Rahul",
        "assistantPhone": "+91-22-2632-5679"
      },
      "preferences": {
        "preferredTime": "4:00 PM - 6:00 PM",
        "preferredDays": ["Monday", "Wednesday", "Friday"],
        "preferredContactMethod": "Phone + Visit",
        "languages": ["English", "Hindi", "Gujarati"],
        "communicationNotes": "Very receptive to new products. Values patient feedback."
      },
      "practiceMetrics": {
        "patientVolume": 100,
        "avgConsultTime": 12,
        "prescriptionPotential": "High",
        "influenceLevel": "Regional Influencer"
      },
      "productAffinities": {
        "currentlyPrescribing": ["DiabeCare", "ImmunoShield", "GastroEase"],
        "interested": ["RespiClear"],
        "competitor": ["Janumet"]
      }
    },
    {
      "id": "DOC-003",
      "personalInfo": {
        "name": "Dr. Rajesh Pandey",
        "age": 48,
        "gender": "Male",
        "photo": "https://api.dicebear.com/7.x/personas/svg?seed=RajeshPandey&backgroundColor=b6e3f4"
      },
      "professionalInfo": {
        "specialty": "Cardiology",
        "subSpecialty": "Heart Failure & Transplant",
        "tier": "A",
        "experience": 22,
        "medicalSchool": "AIIMS, New Delhi",
        "fellowships": ["European Society of Cardiology", "Heart Failure Association"],
        "registrationNumber": "DL-34567"
      },
      "practiceInfo": {
        "clinic": "Heart Care & Diagnostics",
        "hospital": "Max Super Specialty",
        "address": "Lajpat Nagar II, New Delhi 110024",
        "territory": "Delhi NCR",
        "coordinates": { "lat": 28.5677, "lng": 77.2433 }
      },
      "contactInfo": {
        "phone": "+91-11-2981-4567",
        "email": "dr.pandey@maxheart.in",
        "assistant": "Ms. Anjali",
        "assistantPhone": "+91-11-2981-4568"
      },
      "preferences": {
        "preferredTime": "11:00 AM - 1:00 PM",
        "preferredDays": ["Tuesday", "Thursday", "Saturday"],
        "preferredContactMethod": "In-person",
        "languages": ["English", "Hindi"],
        "communicationNotes": "Academic mindset. Interested in research collaboration."
      },
      "practiceMetrics": {
        "patientVolume": 120,
        "avgConsultTime": 18,
        "prescriptionPotential": "Very High",
        "influenceLevel": "National KOL"
      },
      "productAffinities": {
        "currentlyPrescribing": ["CardioMax", "DiabeCare"],
        "interested": [],
        "competitor": ["Caduet", "Amlodac-AT"]
      }
    },
    {
      "id": "DOC-004",
      "personalInfo": {
        "name": "Dr. Lakshmi Krishna",
        "age": 44,
        "gender": "Female",
        "photo": "https://api.dicebear.com/7.x/personas/svg?seed=LakshmiKrishna&backgroundColor=c1f0c1"
      },
      "professionalInfo": {
        "specialty": "Neurology",
        "subSpecialty": "Movement Disorders",
        "tier": "A",
        "experience": 17,
        "medicalSchool": "Osmania Medical College",
        "fellowships": ["Indian Academy of Neurology", "Movement Disorder Society"],
        "registrationNumber": "TS-45678"
      },
      "practiceInfo": {
        "clinic": "Neuro Care Hyderabad",
        "hospital": "Apollo Hospitals",
        "address": "Road No. 36, Jubilee Hills, Hyderabad 500033",
        "territory": "Hyderabad",
        "coordinates": { "lat": 17.4319, "lng": 78.4072 }
      },
      "contactInfo": {
        "phone": "+91-40-2360-5678",
        "email": "dr.lakshmi@apolloneuro.in",
        "assistant": "Ms. Kavitha",
        "assistantPhone": "+91-40-2360-5679"
      },
      "preferences": {
        "preferredTime": "11:00 AM - 1:00 PM",
        "preferredDays": ["Monday", "Wednesday"],
        "preferredContactMethod": "Email + Visit",
        "languages": ["English", "Hindi", "Telugu"],
        "communicationNotes": "Detail-oriented. Appreciates case discussions."
      },
      "practiceMetrics": {
        "patientVolume": 85,
        "avgConsultTime": 20,
        "prescriptionPotential": "High",
        "influenceLevel": "Regional KOL"
      },
      "productAffinities": {
        "currentlyPrescribing": ["NeuroPlex", "SleepWell"],
        "interested": ["DiabeCare"],
        "competitor": ["Keppra"]
      }
    }
  ],
  "visits": {
    "DOC-001": [
      {
        "visitId": "V001-001",
        "date": "2024-12-28",
        "type": "Detailing",
        "product": "CardioMax",
        "rep": "Priya Sharma",
        "duration": 25,
        "outcome": "Very Positive",
        "notes": "Discussed new clinical trial data. Very interested in efficacy for resistant hypertension.",
        "samplesGiven": 10,
        "literatureShared": ["CardioMax MOA brochure", "ASCOT-LLA summary"],
        "nextAction": "Send research papers",
        "nextActionDue": "2025-01-03",
        "competitorMentioned": null,
        "objections": null,
        "commitments": ["Will try on 5 new patients"]
      },
      {
        "visitId": "V001-002",
        "date": "2024-12-20",
        "type": "Sample Drop",
        "product": "CardioMax",
        "rep": "Priya Sharma",
        "duration": 10,
        "outcome": "Positive",
        "notes": "Quick sample replenishment. Mentioned positive patient feedback.",
        "samplesGiven": 20,
        "literatureShared": [],
        "nextAction": "Schedule detailed meeting",
        "nextActionDue": "2024-12-28",
        "competitorMentioned": null,
        "objections": null,
        "commitments": []
      },
      {
        "visitId": "V001-003",
        "date": "2024-12-12",
        "type": "CME Invite",
        "product": "CardioMax",
        "rep": "Priya Sharma",
        "duration": 15,
        "outcome": "Committed to Try",
        "notes": "Invited to cardiology symposium. Confirmed attendance.",
        "samplesGiven": 0,
        "literatureShared": ["CME agenda", "Speaker profiles"],
        "nextAction": "Send calendar invite",
        "nextActionDue": "2024-12-15",
        "competitorMentioned": null,
        "objections": null,
        "commitments": ["Will attend and present case study"]
      },
      {
        "visitId": "V001-004",
        "date": "2024-12-05",
        "type": "Detailing",
        "product": "DiabeCare",
        "rep": "Priya Sharma",
        "duration": 20,
        "outcome": "Neutral",
        "notes": "Discussed diabetic cardiac patients. Wants more data on combination therapy.",
        "samplesGiven": 5,
        "literatureShared": ["DiabeCare prescribing info"],
        "nextAction": "Prepare combination data",
        "nextActionDue": "2024-12-12",
        "competitorMentioned": "Janumet",
        "objections": ["Needs more real-world evidence"],
        "commitments": []
      },
      {
        "visitId": "V001-005",
        "date": "2024-11-28",
        "type": "Follow-up",
        "product": "CardioMax",
        "rep": "Priya Sharma",
        "duration": 15,
        "outcome": "Positive",
        "notes": "Follow-up on previous samples. 3 patients showed improvement.",
        "samplesGiven": 0,
        "literatureShared": [],
        "nextAction": "Get patient testimonials",
        "nextActionDue": "2024-12-05",
        "competitorMentioned": null,
        "objections": null,
        "commitments": ["Will share anonymized case data"]
      },
      {
        "visitId": "V001-006",
        "date": "2024-11-20",
        "type": "Detailing",
        "product": "CardioMax",
        "rep": "Priya Sharma",
        "duration": 30,
        "outcome": "Very Positive",
        "notes": "Deep dive into mechanism of action. Asked technical questions.",
        "samplesGiven": 15,
        "literatureShared": ["MOA detailed presentation", "Phase 3 trial results"],
        "nextAction": "Arrange KOL meet",
        "nextActionDue": "2024-11-28",
        "competitorMentioned": null,
        "objections": null,
        "commitments": ["Interested in advisory board"]
      },
      {
        "visitId": "V001-007",
        "date": "2024-11-12",
        "type": "Product Launch",
        "product": "CardioMax",
        "rep": "Priya Sharma",
        "duration": 45,
        "outcome": "Very Positive",
        "notes": "New formulation launch presentation. Impressed with bioavailability data.",
        "samplesGiven": 25,
        "literatureShared": ["Launch kit", "Bioequivalence study"],
        "nextAction": "Monitor initial prescriptions",
        "nextActionDue": "2024-11-20",
        "competitorMentioned": "Caduet",
        "objections": null,
        "commitments": ["Will make CardioMax first choice for new patients"]
      },
      {
        "visitId": "V001-008",
        "date": "2024-11-05",
        "type": "Sample Drop",
        "product": "DiabeCare",
        "rep": "Priya Sharma",
        "duration": 10,
        "outcome": "Positive",
        "notes": "Regular sample drop for diabetic patients.",
        "samplesGiven": 10,
        "literatureShared": [],
        "nextAction": "Check stock levels",
        "nextActionDue": "2024-11-12",
        "competitorMentioned": null,
        "objections": null,
        "commitments": []
      },
      {
        "visitId": "V001-009",
        "date": "2024-10-28",
        "type": "Detailing",
        "product": "CardioMax",
        "rep": "Priya Sharma",
        "duration": 20,
        "outcome": "Positive",
        "notes": "Discussed cost-effectiveness. Appreciated patient assistance program.",
        "samplesGiven": 10,
        "literatureShared": ["PAP brochure", "Cost comparison chart"],
        "nextAction": "Share PAP details",
        "nextActionDue": "2024-11-05",
        "competitorMentioned": null,
        "objections": ["Price concern for some patients"],
        "commitments": ["Will refer eligible patients to PAP"]
      },
      {
        "visitId": "V001-010",
        "date": "2024-10-20",
        "type": "CME Invite",
        "product": "CardioMax",
        "rep": "Priya Sharma",
        "duration": 12,
        "outcome": "Neutral",
        "notes": "Invited to regional CME. Will confirm later.",
        "samplesGiven": 0,
        "literatureShared": ["CME invitation"],
        "nextAction": "Follow up on confirmation",
        "nextActionDue": "2024-10-28",
        "competitorMentioned": null,
        "objections": ["Schedule conflict possible"],
        "commitments": []
      }
    ],
    "DOC-002": [
      {
        "visitId": "V002-001",
        "date": "2024-12-26",
        "type": "Detailing",
        "product": "DiabeCare",
        "rep": "Priya Sharma",
        "duration": 20,
        "outcome": "Positive",
        "notes": "Discussed new once-daily formulation. Interested for elderly patients.",
        "samplesGiven": 15,
        "literatureShared": ["Elderly patient dosing guide"],
        "nextAction": "Send elderly patient data",
        "nextActionDue": "2025-01-02",
        "competitorMentioned": null,
        "objections": null,
        "commitments": ["Will try on 10 elderly patients"]
      },
      {
        "visitId": "V002-002",
        "date": "2024-12-18",
        "type": "Sample Drop",
        "product": "ImmunoShield",
        "rep": "Priya Sharma",
        "duration": 8,
        "outcome": "Positive",
        "notes": "Seasonal immunity boosters for OPD patients.",
        "samplesGiven": 30,
        "literatureShared": [],
        "nextAction": "Track prescription trend",
        "nextActionDue": "2024-12-26",
        "competitorMentioned": null,
        "objections": null,
        "commitments": []
      },
      {
        "visitId": "V002-003",
        "date": "2024-12-10",
        "type": "Detailing",
        "product": "DiabeCare",
        "rep": "Priya Sharma",
        "duration": 25,
        "outcome": "Very Positive",
        "notes": "Comprehensive metabolic discussion. Will try on 10 new patients.",
        "samplesGiven": 20,
        "literatureShared": ["Metabolic syndrome management guide", "Patient education materials"],
        "nextAction": "Follow up in 2 weeks",
        "nextActionDue": "2024-12-24",
        "competitorMentioned": "Janumet",
        "objections": null,
        "commitments": ["Will switch suitable patients from competitor"]
      },
      {
        "visitId": "V002-004",
        "date": "2024-12-02",
        "type": "Follow-up",
        "product": "DiabeCare",
        "rep": "Priya Sharma",
        "duration": 15,
        "outcome": "Committed to Try",
        "notes": "Positive feedback from trial patients. Increasing prescriptions.",
        "samplesGiven": 0,
        "literatureShared": [],
        "nextAction": "Collect success stories",
        "nextActionDue": "2024-12-10",
        "competitorMentioned": null,
        "objections": null,
        "commitments": ["Will recommend to colleagues"]
      },
      {
        "visitId": "V002-005",
        "date": "2024-11-25",
        "type": "CME Invite",
        "product": "ImmunoShield",
        "rep": "Priya Sharma",
        "duration": 10,
        "outcome": "Positive",
        "notes": "Immunology webinar invitation accepted.",
        "samplesGiven": 0,
        "literatureShared": ["Webinar details"],
        "nextAction": "Send webinar link",
        "nextActionDue": "2024-11-28",
        "competitorMentioned": null,
        "objections": null,
        "commitments": ["Will attend webinar"]
      },
      {
        "visitId": "V002-006",
        "date": "2024-11-18",
        "type": "Detailing",
        "product": "GastroEase",
        "rep": "Priya Sharma",
        "duration": 18,
        "outcome": "Neutral",
        "notes": "Discussed GI complaints in diabetic patients. Needs more evidence.",
        "samplesGiven": 10,
        "literatureShared": ["GastroEase clinical summary"],
        "nextAction": "Prepare GI-diabetes correlation data",
        "nextActionDue": "2024-11-25",
        "competitorMentioned": "Pan-D",
        "objections": ["Already using competitor product"],
        "commitments": []
      },
      {
        "visitId": "V002-007",
        "date": "2024-11-10",
        "type": "Sample Drop",
        "product": "DiabeCare",
        "rep": "Priya Sharma",
        "duration": 10,
        "outcome": "Positive",
        "notes": "Regular replenishment. Stock running low.",
        "samplesGiven": 25,
        "literatureShared": [],
        "nextAction": "Increase sample allocation",
        "nextActionDue": "2024-11-18",
        "competitorMentioned": null,
        "objections": null,
        "commitments": []
      },
      {
        "visitId": "V002-008",
        "date": "2024-11-02",
        "type": "Detailing",
        "product": "ImmunoShield",
        "rep": "Priya Sharma",
        "duration": 22,
        "outcome": "Positive",
        "notes": "Winter season preparation. Bulk samples requested.",
        "samplesGiven": 40,
        "literatureShared": ["Winter immunity guide", "Dosing recommendations"],
        "nextAction": "Arrange bulk delivery",
        "nextActionDue": "2024-11-10",
        "competitorMentioned": null,
        "objections": null,
        "commitments": ["Will recommend to all patients"]
      },
      {
        "visitId": "V002-009",
        "date": "2024-10-26",
        "type": "Follow-up",
        "product": "DiabeCare",
        "rep": "Priya Sharma",
        "duration": 12,
        "outcome": "Positive",
        "notes": "Patient compliance feedback - positive.",
        "samplesGiven": 0,
        "literatureShared": [],
        "nextAction": "Document testimonials",
        "nextActionDue": "2024-11-02",
        "competitorMentioned": null,
        "objections": null,
        "commitments": []
      },
      {
        "visitId": "V002-010",
        "date": "2024-10-18",
        "type": "Product Launch",
        "product": "ImmunoShield",
        "rep": "Priya Sharma",
        "duration": 35,
        "outcome": "Very Positive",
        "notes": "New immunity booster launch. Very enthusiastic about formulation.",
        "samplesGiven": 50,
        "literatureShared": ["Product launch kit", "Clinical studies summary"],
        "nextAction": "Monitor launch performance",
        "nextActionDue": "2024-10-26",
        "competitorMentioned": "Zincovit",
        "objections": null,
        "commitments": ["Will replace competitor product"]
      }
    ],
    "DOC-003": [
      {
        "visitId": "V003-001",
        "date": "2024-12-22",
        "type": "Detailing",
        "product": "CardioMax",
        "rep": "Rajesh Kumar",
        "duration": 30,
        "outcome": "Very Positive",
        "notes": "Heart failure management discussion. Keen on trial for advanced HF patients.",
        "samplesGiven": 20,
        "literatureShared": ["HF management protocol", "CardioMax in HF study"],
        "nextAction": "Arrange HF specialist meet",
        "nextActionDue": "2024-12-30",
        "competitorMentioned": null,
        "objections": null,
        "commitments": ["Will design protocol for HF patients"]
      },
      {
        "visitId": "V003-002",
        "date": "2024-12-15",
        "type": "Sample Drop",
        "product": "CardioMax",
        "rep": "Rajesh Kumar",
        "duration": 12,
        "outcome": "Positive",
        "notes": "ICU stock replenishment.",
        "samplesGiven": 30,
        "literatureShared": [],
        "nextAction": "Check ICU feedback",
        "nextActionDue": "2024-12-22",
        "competitorMentioned": null,
        "objections": null,
        "commitments": []
      },
      {
        "visitId": "V003-003",
        "date": "2024-12-08",
        "type": "CME Invite",
        "product": "CardioMax",
        "rep": "Rajesh Kumar",
        "duration": 20,
        "outcome": "Committed to Try",
        "notes": "National cardiology conference speaker invite.",
        "samplesGiven": 0,
        "literatureShared": ["Conference agenda", "Speaker guidelines"],
        "nextAction": "Coordinate with medical affairs",
        "nextActionDue": "2024-12-15",
        "competitorMentioned": null,
        "objections": null,
        "commitments": ["Will present CardioMax data at conference"]
      },
      {
        "visitId": "V003-004",
        "date": "2024-12-01",
        "type": "Detailing",
        "product": "DiabeCare",
        "rep": "Rajesh Kumar",
        "duration": 25,
        "outcome": "Positive",
        "notes": "Cardiac diabetics discussion. Interested in combination approach.",
        "samplesGiven": 15,
        "literatureShared": ["Cardio-diabetic management guide"],
        "nextAction": "Prepare cardio-diabetic protocol",
        "nextActionDue": "2024-12-08",
        "competitorMentioned": null,
        "objections": null,
        "commitments": ["Will consider for cardio-diabetic patients"]
      },
      {
        "visitId": "V003-005",
        "date": "2024-11-24",
        "type": "Follow-up",
        "product": "CardioMax",
        "rep": "Rajesh Kumar",
        "duration": 18,
        "outcome": "Very Positive",
        "notes": "Excellent results in 5 transplant patients.",
        "samplesGiven": 0,
        "literatureShared": [],
        "nextAction": "Document case studies",
        "nextActionDue": "2024-12-01",
        "competitorMentioned": null,
        "objections": null,
        "commitments": ["Will provide case study data for publication"]
      },
      {
        "visitId": "V003-006",
        "date": "2024-11-17",
        "type": "Detailing",
        "product": "CardioMax",
        "rep": "Rajesh Kumar",
        "duration": 35,
        "outcome": "Very Positive",
        "notes": "Deep technical discussion on mechanism. Wants to write research paper.",
        "samplesGiven": 25,
        "literatureShared": ["Research collaboration proposal", "Publication guidelines"],
        "nextAction": "Connect with medical writing team",
        "nextActionDue": "2024-11-24",
        "competitorMentioned": null,
        "objections": null,
        "commitments": ["Will co-author research paper"]
      },
      {
        "visitId": "V003-007",
        "date": "2024-11-10",
        "type": "Product Launch",
        "product": "CardioMax",
        "rep": "Rajesh Kumar",
        "duration": 50,
        "outcome": "Very Positive",
        "notes": "Premium launch event. Standing ovation for presentation.",
        "samplesGiven": 40,
        "literatureShared": ["Launch kit", "Clinical compendium"],
        "nextAction": "Leverage as KOL",
        "nextActionDue": "2024-11-17",
        "competitorMentioned": null,
        "objections": null,
        "commitments": ["Will be brand ambassador"]
      },
      {
        "visitId": "V003-008",
        "date": "2024-11-03",
        "type": "Sample Drop",
        "product": "DiabeCare",
        "rep": "Rajesh Kumar",
        "duration": 10,
        "outcome": "Neutral",
        "notes": "Routine sample drop.",
        "samplesGiven": 15,
        "literatureShared": [],
        "nextAction": "Schedule detailed meeting",
        "nextActionDue": "2024-11-10",
        "competitorMentioned": null,
        "objections": null,
        "commitments": []
      },
      {
        "visitId": "V003-009",
        "date": "2024-10-27",
        "type": "CME Invite",
        "product": "CardioMax",
        "rep": "Rajesh Kumar",
        "duration": 15,
        "outcome": "Positive",
        "notes": "Regional CME moderator invitation.",
        "samplesGiven": 0,
        "literatureShared": ["CME details"],
        "nextAction": "Send agenda",
        "nextActionDue": "2024-11-03",
        "competitorMentioned": null,
        "objections": null,
        "commitments": ["Will moderate panel discussion"]
      },
      {
        "visitId": "V003-010",
        "date": "2024-10-20",
        "type": "Detailing",
        "product": "CardioMax",
        "rep": "Rajesh Kumar",
        "duration": 28,
        "outcome": "Positive",
        "notes": "Discussed real-world evidence. Requested hospital data.",
        "samplesGiven": 20,
        "literatureShared": ["RWE summary", "Hospital formulary request"],
        "nextAction": "Compile RWE report",
        "nextActionDue": "2024-10-27",
        "competitorMentioned": "Caduet",
        "objections": ["Wants hospital-specific data"],
        "commitments": []
      }
    ],
    "DOC-004": [
      {
        "visitId": "V004-001",
        "date": "2024-12-20",
        "type": "Detailing",
        "product": "NeuroPlex",
        "rep": "Ananya Reddy",
        "duration": 25,
        "outcome": "Positive",
        "notes": "Movement disorder management. Interested in Parkinson's application.",
        "samplesGiven": 15,
        "literatureShared": ["Parkinson's management guide", "NeuroPlex in PD study"],
        "nextAction": "Send Parkinson's data",
        "nextActionDue": "2024-12-27",
        "competitorMentioned": null,
        "objections": null,
        "commitments": ["Will try on 3 PD patients"]
      },
      {
        "visitId": "V004-002",
        "date": "2024-12-12",
        "type": "Sample Drop",
        "product": "NeuroPlex",
        "rep": "Ananya Reddy",
        "duration": 10,
        "outcome": "Positive",
        "notes": "OPD stock replenishment.",
        "samplesGiven": 20,
        "literatureShared": [],
        "nextAction": "Track usage",
        "nextActionDue": "2024-12-20",
        "competitorMentioned": null,
        "objections": null,
        "commitments": []
      },
      {
        "visitId": "V004-003",
        "date": "2024-12-05",
        "type": "CME Invite",
        "product": "SleepWell",
        "rep": "Ananya Reddy",
        "duration": 15,
        "outcome": "Committed to Try",
        "notes": "Sleep disorders symposium speaker invite.",
        "samplesGiven": 0,
        "literatureShared": ["Symposium agenda"],
        "nextAction": "Coordinate logistics",
        "nextActionDue": "2024-12-12",
        "competitorMentioned": null,
        "objections": null,
        "commitments": ["Will speak on sleep in neurological disorders"]
      },
      {
        "visitId": "V004-004",
        "date": "2024-11-28",
        "type": "Detailing",
        "product": "SleepWell",
        "rep": "Ananya Reddy",
        "duration": 22,
        "outcome": "Very Positive",
        "notes": "Discussed sleep architecture in neuro patients. Very impressed.",
        "samplesGiven": 20,
        "literatureShared": ["Sleep architecture study", "SleepWell MOA"],
        "nextAction": "Arrange neurology meet",
        "nextActionDue": "2024-12-05",
        "competitorMentioned": null,
        "objections": null,
        "commitments": ["Will recommend to all insomnia patients"]
      },
      {
        "visitId": "V004-005",
        "date": "2024-11-20",
        "type": "Follow-up",
        "product": "NeuroPlex",
        "rep": "Ananya Reddy",
        "duration": 18,
        "outcome": "Positive",
        "notes": "Good response in essential tremor patients.",
        "samplesGiven": 0,
        "literatureShared": [],
        "nextAction": "Document cases",
        "nextActionDue": "2024-11-28",
        "competitorMentioned": null,
        "objections": null,
        "commitments": ["Will share patient outcomes"]
      },
      {
        "visitId": "V004-006",
        "date": "2024-11-12",
        "type": "Detailing",
        "product": "NeuroPlex",
        "rep": "Ananya Reddy",
        "duration": 30,
        "outcome": "Very Positive",
        "notes": "Deep dive into dopamine pathway. Asked for research collaboration.",
        "samplesGiven": 25,
        "literatureShared": ["Dopamine pathway review", "Research proposal"],
        "nextAction": "Connect with R&D",
        "nextActionDue": "2024-11-20",
        "competitorMentioned": null,
        "objections": null,
        "commitments": ["Will participate in clinical study"]
      },
      {
        "visitId": "V004-007",
        "date": "2024-11-05",
        "type": "Product Launch",
        "product": "NeuroPlex",
        "rep": "Ananya Reddy",
        "duration": 45,
        "outcome": "Very Positive",
        "notes": "Regional launch. Key speaker and endorser.",
        "samplesGiven": 35,
        "literatureShared": ["Launch kit", "Prescribing guide"],
        "nextAction": "Leverage testimonial",
        "nextActionDue": "2024-11-12",
        "competitorMentioned": "Keppra",
        "objections": null,
        "commitments": ["Will be regional brand champion"]
      },
      {
        "visitId": "V004-008",
        "date": "2024-10-28",
        "type": "Sample Drop",
        "product": "SleepWell",
        "rep": "Ananya Reddy",
        "duration": 8,
        "outcome": "Positive",
        "notes": "Sleep clinic stock.",
        "samplesGiven": 25,
        "literatureShared": [],
        "nextAction": "Monitor prescription",
        "nextActionDue": "2024-11-05",
        "competitorMentioned": null,
        "objections": null,
        "commitments": []
      },
      {
        "visitId": "V004-009",
        "date": "2024-10-20",
        "type": "CME Invite",
        "product": "NeuroPlex",
        "rep": "Ananya Reddy",
        "duration": 12,
        "outcome": "Positive",
        "notes": "Movement disorder workshop moderator.",
        "samplesGiven": 0,
        "literatureShared": ["Workshop details"],
        "nextAction": "Send materials",
        "nextActionDue": "2024-10-28",
        "competitorMentioned": null,
        "objections": null,
        "commitments": ["Will moderate and present"]
      },
      {
        "visitId": "V004-010",
        "date": "2024-10-12",
        "type": "Detailing",
        "product": "NeuroPlex",
        "rep": "Ananya Reddy",
        "duration": 20,
        "outcome": "Neutral",
        "notes": "Initial introduction. Wants to see more clinical evidence.",
        "samplesGiven": 10,
        "literatureShared": ["Product overview", "Clinical summary"],
        "nextAction": "Prepare evidence dossier",
        "nextActionDue": "2024-10-20",
        "competitorMentioned": "Keppra",
        "objections": ["Concerned about side effect profile"],
        "commitments": []
      }
    ]
  },
  "engagementScoring": {
    "outcomeWeights": {
      "Very Positive": 5,
      "Committed to Try": 4,
      "Positive": 3,
      "Neutral": 2,
      "Needs Follow-up": 1
    },
    "visitTypeWeights": {
      "Product Launch": 5,
      "Detailing": 4,
      "CME Invite": 3,
      "Follow-up": 2,
      "Sample Drop": 1
    },
    "thresholds": {
      "highEngagement": 35,
      "mediumEngagement": 20,
      "lowEngagement": 10
    }
  },
  "aiPromptContext": {
    "personaTypes": [
      "Scientific Skeptic - Needs extensive clinical evidence before adopting new products",
      "Relationship Builder - Values personal connection and trust over data alone",
      "Efficiency Focused - Prioritizes time and wants concise, actionable information",
      "Patient Advocate - Decisions driven primarily by patient outcomes and safety",
      "Innovation Seeker - Early adopter who embraces new treatments enthusiastically",
      "Evidence Driven - Balances clinical evidence with practical experience",
      "Protocol Follower - Adheres strictly to guidelines and established protocols"
    ],
    "engagementFactors": [
      "Visit frequency and consistency",
      "Duration of interactions",
      "Sample utilization patterns",
      "Response to CME invitations",
      "Feedback positivity trends",
      "Product adoption timeline",
      "Competitor mentions frequency",
      "Objection patterns",
      "Commitment follow-through rate"
    ],
    "discussionTopicCategories": [
      "Clinical efficacy data",
      "Safety and tolerability",
      "Cost-effectiveness",
      "Patient compliance",
      "Competitive positioning",
      "Real-world evidence",
      "Practice-specific applications",
      "Research collaboration",
      "CME and education",
      "Patient assistance programs"
    ]
  }
};

